/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef } from 'react';
import ReactMarkdown, { Components } from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import Chart from 'chart.js/auto';
import 'katex/dist/katex.min.css';
import '../../styles/components/MarkdownRenderer.css';

interface MarkdownRendererProps {
  content: string;
  theme?: 'light' | 'dark';
}

interface CodeBlockProps {
  inline?: boolean;
  className?: string;
  children?: React.ReactNode;
  [key: string]: any;
}

interface LinkProps {
  href?: string;
  children?: React.ReactNode;
  title?: string;
  [key: string]: any;
}

const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({ 
  content, 
  theme = 'light' 
}) => {
  const chartRefs = useRef<Map<string, HTMLCanvasElement>>(new Map());
  const chartCounter = useRef(0);
  const blobUrls = useRef<Set<string>>(new Set());

  // Parse chart data from code block
  const parseChartData = (code: string) => {
    try {
      return JSON.parse(code);
    } catch (error) {
      console.warn('Failed to parse chart data:', code, error);
      return null;
    }
  };

  // Enhanced preprocessing to handle **text** bold and *text* italics syntax
  const preprocessContent = (content: string): string => {
    // Handle undefined or null content
    if (!content) return '';
    
    let processed = content;
    
    // Convert **text** pattern to Markdown bold syntax
    processed = processed.replace(/\*\*(\s*)([^*]+?)(\s*)\*\*/g, '**$2**');
    
    // Convert *text* pattern to Markdown italics syntax
    processed = processed.replace(/(?<!\w)\*(\s*)([^*\n]+?)(\s*)\*(?!\*|\w)/g, '*$2*');
    
    // Ensure proper LaTeX delimiters - convert common variations
    processed = processed.replace(/\\\[/g, '$$');
    processed = processed.replace(/\\\]/g, '$$');
    processed = processed.replace(/\\\(/g, '$');
    processed = processed.replace(/\\\)/g, '$');
    
    // Clean up any double dollar signs that might have been created
    processed = processed.replace(/\$\$\$\$/g, '$$');
    
    // Ensure math blocks are on their own lines
    processed = processed.replace(/\$\$(.*?)\$\$/g, '\n\n$$$$$1$$$$\n\n');
    
    // Clean up extra newlines
    processed = processed.replace(/\n\n\n+/g, '\n\n');
    
    return processed.trim();
  };

  // Custom code block component
  const CodeBlock: React.FC<CodeBlockProps> = ({ inline, className, children, ...props }) => {
    const match = /language-(\w+)/.exec(className || '');
    const language = match ? match[1] : '';

    if (!inline && language === 'chart') {
      const chartId = `chart-${chartCounter.current++}`;
      return (
        <div className="chart-container">
          <canvas
            ref={(el) => {
              if (el) chartRefs.current.set(chartId, el);
            }}
            className="chart-canvas"
            aria-label="Chart generated from Markdown"
          />
        </div>
      );
    }

    if (!inline && language) {
      return (
        <SyntaxHighlighter
          style={vscDarkPlus}
          language={language}
          PreTag="div"
          className="code-block"
          {...props}
        >
          {String(children).replace(/\n$/, '')}
        </SyntaxHighlighter>
      );
    }

    return (
      <code className={`inline-code ${className || ''}`} {...props}>
        {children}
      </code>
    );
  };

  // Table components
  const Table: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
    <div className="table-wrapper">
      <table className="markdown-table">{children}</table>
    </div>
  );

  const TableHead: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
    <thead className="table-head">{children}</thead>
  );

  const TableBody: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
    <tbody className="table-body">{children}</tbody>
  );

  const TableRow: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
    <tr className="table-row">{children}</tr>
  );

  const TableHeader: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
    <th className="table-header">{children}</th>
  );

  const TableCell: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
    <td className="table-cell">{children}</td>
  );

  // Typography components
  const Paragraph: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
    <p className="markdown-paragraph">{children}</p>
  );

  const Strong: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
    <strong className="markdown-strong">{children}</strong>
  );

  const Emphasis: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
    <em className="markdown-emphasis">{children}</em>
  );

  const Heading1: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
    <h1 className="markdown-h1">{children}</h1>
  );

  const Heading2: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
    <h2 className="markdown-h2">{children}</h2>
  );

  const Heading3: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
    <h3 className="markdown-h3">{children}</h3>
  );

  const Heading4: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
    <h4 className="markdown-h4">{children}</h4>
  );

  const Heading5: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
    <h5 className="markdown-h5">{children}</h5>
  );

  const Heading6: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
    <h6 className="markdown-h6">{children}</h6>
  );

  const BlockQuote: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
    <blockquote className="markdown-blockquote">{children}</blockquote>
  );

  const Link: React.FC<LinkProps> = ({ 
    href, 
    children, 
    title,
    ...props 
  }) => (
    <a 
      href={href} 
      title={title}
      className="markdown-link" 
      target="_blank" 
      rel="noopener noreferrer"
      {...props}
    >
      {children}
    </a>
  );

  // Type guard to check if value is a Blob
  const isBlob = (value: any): value is Blob => {
    return value instanceof Blob || 
           (value && typeof value === 'object' && 
            typeof value.arrayBuffer === 'function' && 
            typeof value.type === 'string');
  };

  // Image component that handles both string URLs and Blobs
  const Image: React.FC<React.ImgHTMLAttributes<HTMLImageElement>> = (props) => {
    const { src, alt, title, ...restProps } = props;
    
    const [imageSrc, setImageSrc] = React.useState<string | undefined>(() => {
      if (!src) return undefined;
      
      // Check if src is a Blob
      if (isBlob(src)) {
        const url = URL.createObjectURL(src);
        blobUrls.current.add(url);
        return url;
      }
      
      // Otherwise assume it's a string
      return src as string;
    });
    
    // Clean up blob URLs on unmount
    React.useEffect(() => {
      return () => {
        if (imageSrc && blobUrls.current.has(imageSrc)) {
          URL.revokeObjectURL(imageSrc);
          blobUrls.current.delete(imageSrc);
        }
      };
    }, [imageSrc]);
    
    // Update imageSrc when src prop changes
    React.useEffect(() => {
      if (!src) {
        setImageSrc(undefined);
        return;
      }
      
      if (isBlob(src)) {
        const url = URL.createObjectURL(src);
        blobUrls.current.add(url);
        setImageSrc(url);
      } else {
        setImageSrc(src as string);
      }
    }, [src]);
    
    return (
      <img 
        src={imageSrc} 
        alt={alt} 
        title={title} 
        className="markdown-image"
        loading="lazy"
        {...restProps}
      />
    );
  };

  const UnorderedList: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
    <ul className="markdown-ul">{children}</ul>
  );

  const OrderedList: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
    <ol className="markdown-ol">{children}</ol>
  );

  const ListItem: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
    <li className="markdown-li">{children}</li>
  );

  const HorizontalRule: React.FC = () => (
    <hr className="markdown-hr" />
  );

  // Chart rendering effect
  useEffect(() => {
    // Capture the ref value at the start of the effect so cleanup uses the same snapshot
    const currentChartRefs = chartRefs.current;

    const timeoutId = setTimeout(() => {
      currentChartRefs.forEach((canvas, chartId) => {
        const chartMatches = content?.match(/```chart\n([\s\S]*?)\n```/g);
        if (chartMatches && chartMatches.length > 0) {
          const chartIndex = Array.from(currentChartRefs.keys()).indexOf(chartId);
          const currentChartMatch = chartMatches[chartIndex];
          
          if (currentChartMatch && canvas) {
            const chartCodeMatch = currentChartMatch.match(/```chart\n([\s\S]*?)\n```/);
            if (chartCodeMatch && chartCodeMatch[1]) {
              const chartData = parseChartData(chartCodeMatch[1]);
              if (chartData) {
                // Destroy existing chart
                const existingChart = Chart.getChart(canvas);
                if (existingChart) {
                  existingChart.destroy();
                }

                new Chart(canvas, {
                  type: chartData.type,
                  data: chartData.data,
                  options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        display: chartData.options?.legend?.display ?? true,
                        position: chartData.options?.legend?.position ?? 'top',
                        labels: {
                          font: { 
                            family: "'Segoe UI', 'Roboto', sans-serif", 
                            size: 12 
                          },
                          color: theme === 'dark' ? '#e4e4e7' : '#374151',
                        },
                      },
                      title: {
                        display: !!chartData.options?.title?.text,
                        text: chartData.options?.title?.text ?? '',
                        font: {
                          family: "'Segoe UI', 'Roboto', sans-serif",
                          size: 16,
                          weight: 'bold',
                        },
                        color: theme === 'dark' ? '#f9fafb' : '#111827',
                      },
                    },
                    scales: chartData.options?.scales ?? {
                      x: {
                        ticks: { 
                          color: theme === 'dark' ? '#d1d5db' : '#6b7280',
                          font: { family: "'Segoe UI', 'Roboto', sans-serif" }
                        },
                        grid: { 
                          color: theme === 'dark' ? '#374151' : '#e5e7eb'
                        },
                      },
                      y: {
                        ticks: { 
                          color: theme === 'dark' ? '#d1d5db' : '#6b7280',
                          font: { family: "'Segoe UI', 'Roboto', sans-serif" }
                        },
                        grid: { 
                          color: theme === 'dark' ? '#374151' : '#e5e7eb'
                        },
                      },
                    },
                    animation: { 
                      duration: 800, 
                      easing: 'easeOutQuart' 
                    },
                  },
                });
              }
            }
          }
        }
      });
    }, 100); // Small delay to ensure DOM is ready

    return () => {
      clearTimeout(timeoutId);
      // Use the captured ref snapshot in cleanup
      currentChartRefs.forEach((canvas) => {
        const chart = Chart.getChart(canvas);
        if (chart) {
          chart.destroy();
        }
      });
    };
  }, [content, theme]);

  // Clean up all blob URLs on unmount
  useEffect(() => {
    return () => {
      blobUrls.current.forEach((url) => {
        URL.revokeObjectURL(url);
      });
      blobUrls.current.clear();
    };
  }, []);

  // Component mapping with proper typing
  const components: Components = {
    // Code
    code: CodeBlock,
    
    // Typography
    p: Paragraph,
    strong: Strong,
    em: Emphasis,
    h1: Heading1,
    h2: Heading2,
    h3: Heading3,
    h4: Heading4,
    h5: Heading5,
    h6: Heading6,
    blockquote: BlockQuote,
    
    // Links and images
    a: Link,
    img: Image,
    
    // Lists
    ul: UnorderedList,
    ol: OrderedList,
    li: ListItem,
    
    // Other
    hr: HorizontalRule,
    
    // Tables
    table: Table,
    thead: TableHead,
    tbody: TableBody,
    tr: TableRow,
    th: TableHeader,
    td: TableCell,
  };

  // Handle empty or undefined content
  if (!content) {
    return (
      <div className={`markdown-renderer theme-${theme}`}>
        <div className="empty-content">No content to display</div>
      </div>
    );
  }

  return (
    <div className={`markdown-renderer theme-${theme}`}>
      <div className="katex-styles">
        <ReactMarkdown
          components={components}
          remarkPlugins={[remarkMath, remarkGfm]}
          rehypePlugins={[
            [rehypeKatex, { 
              strict: false,
              trust: true,
              macros: {
                "\\RR": "\\mathbb{R}",
                "\\NN": "\\mathbb{N}",
                "\\ZZ": "\\mathbb{Z}",
                "\\QQ": "\\mathbb{Q}",
                "\\CC": "\\mathbb{C}",
              }
            }], 
            rehypeRaw
          ]}
        >
          {preprocessContent(content)}
        </ReactMarkdown>
      </div>
    </div>
  );
};

export default MarkdownRenderer;